import React from 'react'
import { alpha, Box, Button, Chip, Group, Text } from '@mantine/core'
import { useForm } from '@mantine/form'
import dayjs from 'dayjs'
import { DatePresetFilter } from './DatePresetFilter'
import { DATE_PRESET_OPTIONS, DateRange, resolvePresetDateRange } from '../utils/datePreset'

interface Props {
  onSubmit: (values: FormData) => Promise<void> | void

  // 一度に指定できる期間の上限（ヶ月）。クライアントのデータ量から決まる。null は取得中
  periodLimitMonths: number | null

  isSubmitting?: boolean
  submitError?: string | null
}

export interface FormData {
  reportType: ReportType
  startDate: string | null
  endDate: string | null
}

export type ReportType = 'cv' | 'service'

const buildChipStyles = (isSelected: boolean) => ({
  iconWrapper: { display: 'none' },
  label: {
    '--chip-checked-padding': 'var(--chip-padding)',
    backgroundColor: isSelected ? alpha('var(--mantine-color-invy-6)', 0.12) : '#fff',
    color: isSelected ? 'var(--mantine-color-invy-6)' : undefined,
    border: isSelected ? '2px solid var(--mantine-color-invy-6)' : undefined,
    height: "100%",
    padding: "16px 20px"
  } as React.CSSProperties,
})

const SERVICE_REPORT_MIN_DATE = '2018-10-18'

// 上限を超えるとサーバーがデータを処理しきれずサイト全体が停止するため、送信前に弾く
const exceedsPeriodLimit = ([from, to]: [string, string], limitMonths: number) =>
  dayjs(to).isAfter(dayjs(from).add(limitMonths, 'month'))

export const ReportForm = ({
  onSubmit,
  periodLimitMonths,
  isSubmitting = false,
  submitError,
}: Props) => {
  const form = useForm<{
    reportType: ReportType
    datePreset: string
    dateRange: DateRange
  }>({
    initialValues: {
      reportType: 'cv',
      datePreset: 'custom',
      dateRange: [null, null],
    }
  })

  const hiddenPresets = DATE_PRESET_OPTIONS
    .map(({ value }) => value)
    .filter((value) => {
      if (periodLimitMonths === null) return false
      const range = resolvePresetDateRange(value, [null, null])
      return range !== null && exceedsPeriodLimit(range, periodLimitMonths)
    })

  const resolved = resolvePresetDateRange(form.values.datePreset, form.values.dateRange)
  const today = dayjs().format('YYYY-MM-DD')
  const isServiceOutOfRange =
    form.values.reportType === 'service' &&
    resolved !== null &&
    resolved[0] < SERVICE_REPORT_MIN_DATE
  const isOverPeriodLimit =
    periodLimitMonths !== null &&
    resolved !== null &&
    exceedsPeriodLimit(resolved, periodLimitMonths)
  const errorMessage = isServiceOutOfRange
    ? '2018.10.18より前の日付は選択できません'
    : isOverPeriodLimit
      ? `出力できる期間は${periodLimitMonths}ヶ月以内です。期間を分けてダウンロードしてください`
      : null
  const disableSubmit =
    resolved === null ||
    periodLimitMonths === null ||
    isServiceOutOfRange ||
    isOverPeriodLimit ||
    isSubmitting

  return (
    <Box bg="#00000008" p={16} mt={40} bdrs={8}>
      <form onSubmit={form.onSubmit(({ reportType, datePreset, dateRange }) => {
        const range = resolvePresetDateRange(datePreset, dateRange)
        if (!range) return
        onSubmit({ reportType, startDate: range[0], endDate: range[1] })
      })}>
        <Group gap={24}>
          <Text fz={13} fw={600} c="#000000CC" style={{ width: "100px", textAlign: "start" }}>レポート種別</Text>
          <Chip.Group multiple={false} {...form.getInputProps('reportType')}>
            <Group justify='center' gap={8}>
              <Chip
                value="cv"
                variant="outline"
                radius={8}
                styles={buildChipStyles(form.values.reportType === 'cv')}
              >
                <Text fz={13} fw={600}>ゲスト紹介レポート</Text>
                <Text c="#00000099" fz={11}>
                  ゲストのCV状況やページ遷移の確認ができます。<br />
                  ゲストページを開いてからCVに至るまでの流れを確認できます。
                </Text>
              </Chip>
              <Chip
                value="service"
                variant="outline"
                radius={8}
                styles={buildChipStyles(form.values.reportType === 'service')}
              >
                <Text fz={13} fw={600}>紹介者レポート</Text>
                <Text c="#00000099" fz={11}>
                  紹介者の紹介数やページ遷移の状況を確認できます。<br />
                  紹介者ページから広告主サイト訪問までの流れを確認できます。
                </Text>
              </Chip>
            </Group>
          </Chip.Group>
        </Group>

        <Box mt={16}>
          <DatePresetFilter
            value={form.values.datePreset}
            dateRange={form.values.dateRange}
            onChangeValue={(v) => form.setFieldValue('datePreset', v)}
            onChangeDateRange={(r) => form.setFieldValue('dateRange', r)}
            hiddenPresets={hiddenPresets}
            loading={periodLimitMonths === null}
            maxDate={today}
            minDate={form.values.reportType === 'service' ? SERVICE_REPORT_MIN_DATE : undefined}
          />
        </Box>
        {errorMessage && (
          <Text c="red" fz={13} mt={8} ml={124}>{errorMessage}</Text>
        )}

        <Group justify='end' mt={16}>
          <Button
            variant='filled'
            radius='xl'
            type='submit'
            fz={13}
            fw={600}
            miw={120}
            disabled={disableSubmit}
          >
            {isSubmitting ? '処理中です。しばらくお待ちください' : '表示'}
          </Button>
        </Group>
        {submitError && (
          <Text c="red" fz={13} mt={8} ta="end" style={{ whiteSpace: 'pre-wrap' }}>
            {submitError}
          </Text>
        )}
      </form>
    </Box>
  )
}
